import { useActionLogsQuery, ActionLog } from '@app/api/dieter/actionLog';
import { Modal, Paper } from '@mui/material';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface ActionLogModalProps {
  onClose: () => void;
  resourceId: string;
  taskName: string;
}

interface ActionLogGroup {
  id: string;
  userId?: string | null;
  userEmail: string;
  userName?: string;
  logs: ActionLog[];
  firstTimestamp: Date;
  lastTimestamp: Date;
}

const TIME_GROUP_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export function ActionLogModal({ onClose, resourceId, taskName }: ActionLogModalProps) {
  const { t } = useTranslation();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Fetch all action logs and filter by resourceId on client side
  const { data: logsData, loading: logsLoading } = useActionLogsQuery({
    limit: 500, // Fetch enough to capture all logs for this resource
  });

  // Filter logs for this specific resource
  const resourceLogs = (logsData?.actionLogs || []).filter(
    (log) => log.resourceId === resourceId
  );

  // Group logs by user and time proximity
  const groupedLogs = useMemo(() => {
    if (!resourceLogs.length) return [];

    // Sort logs by timestamp (oldest first)
    const sortedLogs = [...resourceLogs].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    const groups: ActionLogGroup[] = [];
    let currentGroup: ActionLogGroup | null = null;

    sortedLogs.forEach((log) => {
      const logTime = new Date(log.createdAt);

      // Check if we can add to current group
      if (
        currentGroup &&
        currentGroup.userEmail === log.userEmail &&
        logTime.getTime() - currentGroup.lastTimestamp.getTime() < TIME_GROUP_THRESHOLD_MS
      ) {
        // Add to existing group
        currentGroup.logs.push(log);
        currentGroup.lastTimestamp = logTime;
      } else {
        // Create new group
        currentGroup = {
          id: `group-${groups.length}`,
          userId: log.userId,
          userEmail: log.userEmail,
          userName: log.userName,
          logs: [log],
          firstTimestamp: logTime,
          lastTimestamp: logTime,
        };
        groups.push(currentGroup);
      }
    });

    // Reverse to show newest first
    return groups.reverse();
  }, [resourceLogs]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAction = (action: string) => {
    // Action translations with German defaults
    const actionTranslations: Record<string, string> = {
      'dpo_task.approve': t('dashboard.dpo.actionlog.actions.dpo_task_approve', 'Abgenommen'),
      'dpo_task.withdraw_approval': t('dashboard.dpo.actionlog.actions.dpo_task_withdraw_approval', 'Abnahme zurückgezogen'),
      'questionnaire.answer': t('dashboard.dpo.actionlog.actions.questionnaire_answer', 'Fragebogen beantwortet'),
      'questionnaire.create': t('dashboard.dpo.actionlog.actions.questionnaire_create', 'Fragebogen erstellt'),
      'questionnaire.update': t('dashboard.dpo.actionlog.actions.questionnaire_update', 'Fragebogen aktualisiert'),
      'questionnaire.complete': t('dashboard.dpo.actionlog.actions.questionnaire_complete', 'Fragebogen abgeschlossen'),
      'questionnaire.delete': t('dashboard.dpo.actionlog.actions.questionnaire_delete', 'Fragebogen gelöscht'),
      'task.complete': t('dashboard.dpo.actionlog.actions.task_complete', 'Aufgabe abgeschlossen'),
      'task.reopen': t('dashboard.dpo.actionlog.actions.task_reopen', 'Aufgabe wiedereröffnet'),
      'file.upload': t('dashboard.dpo.actionlog.actions.file_upload', 'Datei hochgeladen'),
      'file.delete': t('dashboard.dpo.actionlog.actions.file_delete', 'Datei gelöscht'),
      'document.generate': t('dashboard.dpo.actionlog.actions.document_generate', 'Dokument generiert'),
      'document.confirm': t('dashboard.dpo.actionlog.actions.document_confirm', 'Dokument bestätigt'),
      'custom_training.create': t('dashboard.dpo.actionlog.actions.custom_training_create', 'Eigene Schulung angelegt'),
      'custom_training.update': t('dashboard.dpo.actionlog.actions.custom_training_update', 'Eigene Schulung geändert'),
      'custom_training.archive': t('dashboard.dpo.actionlog.actions.custom_training_archive', 'Eigene Schulung archiviert'),
      // Klaus ↔ Dieter Sync (automatisierte Writes der Sync-API, dev/klaus-sync/contract.md)
      'klaus.link.create': t('dashboard.dpo.actionlog.actions.klaus_link_create', 'Mit Klaus-Organisation verknüpft'),
      'klaus.link.delete': t('dashboard.dpo.actionlog.actions.klaus_link_delete', 'Verknüpfung mit Klaus aufgehoben'),
      'klaus.member.add': t('dashboard.dpo.actionlog.actions.klaus_member_add', 'Mitglied über Klaus hinzugefügt'),
      'klaus.member.remove': t('dashboard.dpo.actionlog.actions.klaus_member_remove', 'Mitglied über Klaus entfernt'),
      'klaus.company.create': t('dashboard.dpo.actionlog.actions.klaus_company_create', 'Unternehmen über Klaus angelegt'),
      'klaus.tenant.create': t('dashboard.dpo.actionlog.actions.klaus_tenant_create', 'Mandant aus Klaus überführt'),
    };

    // Return translation if available
    if (actionTranslations[action]) {
      return actionTranslations[action];
    }

    // Fallback to formatting the action string
    const parts = action.split('.');
    return parts.map((part) => part.replace(/_/g, ' ').charAt(0).toUpperCase() + part.replace(/_/g, ' ').slice(1)).join(' ');
  };

  // Check if an answer value indicates an INFO node that was read
  const isKnotenValue = (value: any): boolean => {
    if (typeof value === 'string' && (/^Knoten \d+$/.test(value) || value === 'placeholder')) {
      return true;
    }
    return false;
  };

  // Parse and format questionnaire answer changes
  const formatQuestionnaireChanges = (beforeState: any, afterState: any): React.ReactNode[] => {
    const changes: React.ReactNode[] = [];

    // Try to extract answers arrays
    const beforeAnswers = beforeState?.answers || [];
    const afterAnswers = afterState?.answers || [];

    // Create a map of questions by ID
    const questionMap = new Map();
    [...beforeAnswers, ...afterAnswers].forEach((item: any) => {
      if (item.questionId && !questionMap.has(item.questionId)) {
        questionMap.set(item.questionId, item);
      }
    });

    // For each question, check if there was a change
    questionMap.forEach((_, questionId) => {
      const before = beforeAnswers.find((a: any) => a.questionId === questionId);
      const after = afterAnswers.find((a: any) => a.questionId === questionId);

      const beforeAnswer = before?.answer;
      const afterAnswer = after?.answer;

      // Only show if there was a change
      if (JSON.stringify(beforeAnswer) !== JSON.stringify(afterAnswer)) {
        const questionText = before?.questionText || after?.questionText || 'Unbekannte Frage';
        const isInfoRead = isKnotenValue(afterAnswer);

        changes.push(
          <div key={questionId} className="px-3 py-2 border-b border-gray-100 last:border-b-0">
            <div className="text-sm text-gray-700 font-medium mb-1">{questionText}</div>
            {isInfoRead ? (
              <div className="text-xs text-gray-700">(Gelesen)</div>
            ) : (
              <div className="flex items-center gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Von:</span>
                  <span className={beforeAnswer === null || beforeAnswer === undefined || beforeAnswer === '' ? 'text-gray-400 italic' : 'text-gray-900'}>
                    {formatSimpleValue(beforeAnswer, true)}
                  </span>
                </div>
                <span className="text-gray-400">→</span>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Zu:</span>
                  <span className="text-gray-900">{formatSimpleValue(afterAnswer)}</span>
                </div>
              </div>
            )}
          </div>
        );
      }
    });

    return changes;
  };

  // Format simple values (strings, booleans, numbers)
  const formatSimpleValue = (value: any, showEmptyAsLeer: boolean = false): string => {
    if (value === null || value === undefined || value === '') {
      return showEmptyAsLeer ? '(leer)' : 'null';
    }
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    return JSON.stringify(value);
  };

  // Format non-questionnaire state changes
  const formatGenericChanges = (beforeState: any, afterState: any, action: string): React.ReactNode => {
    // Check if this is a DPO approval change
    if (action.startsWith('dpo_task.') &&
        (beforeState?.isApproved !== undefined || afterState?.isApproved !== undefined)) {
      const beforeApproved = beforeState?.isApproved;
      const afterApproved = afterState?.isApproved;

      return (
        <div className="flex items-center gap-2 text-xs">
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Von:</span>
            <span className="text-gray-900">
              {beforeApproved ? 'Abgenommen' : 'Nicht abgenommen'}
            </span>
          </div>
          <span className="text-gray-400">→</span>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Zu:</span>
            <span className="text-gray-900">
              {afterApproved ? 'Abgenommen' : 'Nicht abgenommen'}
            </span>
          </div>
        </div>
      );
    }

    // For simple values, show inline
    if (
      (typeof beforeState !== 'object' || beforeState === null) &&
      (typeof afterState !== 'object' || afterState === null)
    ) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Von:</span>
            <span className={beforeState === null || beforeState === undefined || beforeState === '' ? 'text-gray-400 italic' : 'text-gray-900'}>
              {formatSimpleValue(beforeState, true)}
            </span>
          </div>
          <span className="text-gray-400">→</span>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Zu:</span>
            <span className="text-gray-900">{formatSimpleValue(afterState)}</span>
          </div>
        </div>
      );
    }

    // For complex objects, show JSON
    return (
      <div className="grid grid-cols-2 gap-4 text-xs">
        {beforeState && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Vorher:</h4>
            <pre className="bg-white p-3 rounded border border-gray-200 overflow-auto max-h-64">
              {JSON.stringify(beforeState, null, 2)}
            </pre>
          </div>
        )}
        {afterState && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Nachher:</h4>
            <pre className="bg-white p-3 rounded border border-gray-200 overflow-auto max-h-64">
              {JSON.stringify(afterState, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <Paper
        sx={{
          position: 'relative',
          width: '90%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '24px',
        }}
      >
        <div>
          <h2 className="text-2xl font-semibold mb-2">{t('dashboard.dpo.actionlog.title', 'Aktionsprotokoll')}</h2>
          <p className="text-gray-600 mb-6">{taskName}</p>

          {logsLoading ? (
            <p className="text-gray-500">{t('common.loading', 'Laden...')}</p>
          ) : resourceLogs.length === 0 ? (
            <p className="text-gray-500">{t('dashboard.dpo.actionlog.no_actions', 'Keine Aktionen gefunden für diese Ressource.')}</p>
          ) : (
            <div className="space-y-3">
              {groupedLogs.map((group) => (
                <div key={group.id} className="border rounded-lg bg-white shadow-sm">
                  {/* Group Header */}
                  <div
                    className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleGroup(group.id)}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <button className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                        {expandedGroups.has(group.id) ? (
                          <span className="text-lg">▼</span>
                        ) : (
                          <span className="text-lg">▶</span>
                        )}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-gray-900">
                            {group.userName || group.userEmail}
                          </span>
                          <span className="text-xs text-gray-500">
                            {group.logs.length} {group.logs.length === 1 ? 'Aktion' : 'Aktionen'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(group.lastTimestamp.toISOString())}
                          </span>
                        </div>
                        {group.userName && (
                          <div className="text-xs text-gray-500">{group.userEmail}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Group Content */}
                  {expandedGroups.has(group.id) && (
                    <div className="border-t border-gray-200 bg-gray-50">
                      {group.logs.map((log, idx) => {
                        // Check if this is a questionnaire action
                        const isQuestionnaireAction = log.action.startsWith('questionnaire.');
                        const hasAnswersData =
                          log.beforeState?.answers || log.afterState?.answers;

                        let questionnaireChanges: React.ReactNode[] = [];
                        if (isQuestionnaireAction && hasAnswersData) {
                          questionnaireChanges = formatQuestionnaireChanges(
                            log.beforeState,
                            log.afterState
                          );
                        }

                        return (
                          <div
                            key={log.id}
                            className={`${idx !== 0 ? 'border-t border-gray-200' : ''} px-6 py-3`}
                          >
                            {/* Individual Log Header */}
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-xs text-gray-500">
                                {formatDate(log.createdAt)}
                              </span>
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                {formatAction(log.action)}
                              </span>
                              {log.automated && (
                                <span className="text-xs text-gray-400 italic">
                                  ({t('dashboard.dpo.actionlog.automated', 'Automatisiert')})
                                </span>
                              )}
                            </div>

                            {/* State Changes */}
                            {questionnaireChanges.length > 0 ? (
                              <div className="bg-white rounded border border-gray-200 divide-y divide-gray-100">
                                {questionnaireChanges}
                              </div>
                            ) : (log.beforeState || log.afterState) ? (
                              <div className="mt-2">
                                {formatGenericChanges(log.beforeState, log.afterState, log.action)}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic mt-1">
                                {t('dashboard.dpo.actionlog.no_state_changes', 'Keine Zustandsänderungen aufgezeichnet.')}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Paper>
    </Modal>
  );
}
